import './style.css';
import type { SpeciesData, PlotData, MetaData, LandmarkData, ComparisonData, ClusterData, PlotSimData } from './types';
import { initClusterLookup } from './clusterPalette';
import 'maplibre-gl/dist/maplibre-gl.css';
import { MapLibreAdapter } from './adapters/MapLibreAdapter';
import { StoryMap }       from './StoryMap';
import { BandOverlay }    from './components/BandOverlay';

// ── Create map container ──────────────────────────────────────────────────────
const app = document.getElementById('app')!;

const mapDiv = document.createElement('div');
mapDiv.id = 'sm-map';
app.appendChild(mapDiv);

// Map header (Planet mosaic label, updated by MapLibreAdapter)
const mapHeader = document.createElement('div');
mapHeader.id = 'sm-map-header';
app.appendChild(mapHeader);

// ── Initialise map ────────────────────────────────────────────────────────────
const mapAdapter = new MapLibreAdapter('sm-map', [-15.85, 29.85], 9);

// ── Load data and mount ───────────────────────────────────────────────────────
async function init(): Promise<void> {
  // Core scene data
  const [species, plots, meta, landmarks, comparison, clusters, plotSims] = await Promise.all([
    fetch('/data/species.json').then(r => r.json()    as Promise<SpeciesData[]>),
    fetch('/data/plots.json').then(r => r.json()      as Promise<PlotData[]>),
    fetch('/data/meta.json').then(r => r.json()       as Promise<MetaData>),
    fetch('/data/landmarks.json').then(r => r.json()  as Promise<LandmarkData[]>),
    fetch('/data/comparison.json').then(r => r.json() as Promise<ComparisonData>),
    fetch('/data/clusters.json').then(r => r.json()   as Promise<ClusterData[]>),
    fetch('/data/plot_sims.json').then(r => r.json()  as Promise<PlotSimData[]>),
  ]);

  // Planet mosaic catalog → load latest as persistent basemap
  const catalog = await fetch(
    'https://storage.googleapis.com/centinela-test-data/planet-mosaics/cogs/catalog.json',
  ).then(r => r.json() as Promise<Record<string, string>>).catch(() => ({} as Record<string, string>));

  const latestKey = Object.keys(catalog).sort().at(-1);
  const latestCog = latestKey ? catalog[latestKey] : '';
  if (latestKey) {
    mapAdapter.addPlanetBasemap(latestCog, latestKey);
  }

  // Sapi boundary (optional — skipped gracefully if file absent)
  const sapiGeo = await fetch('/data/sapi_boundary.geojson')
    .then(r => r.ok ? r.json() : null)
    .catch(() => null);
  if (sapiGeo) mapAdapter.addSapiBoundary(sapiGeo);

  // Climate CSV (optional — SceneClimate renders gracefully when empty)
  const climateCsv = await fetch('/data/climate.csv')
    .then(r => r.ok ? r.text() : '')
    .catch(() => '');

  // Soil carbon inventory CSV
  const soilCsv = await fetch('/data/soil_carbon_inventory.csv')
    .then(r => r.ok ? r.text() : '')
    .catch(() => '');

  // GPP inventory CSV (fetched from GCS)
  const gppCsv = await fetch(
    'https://storage.googleapis.com/centinela-test-data/openclaw/gpw/gpw_gpp_inventory.csv',
  ).then(r => r.ok ? r.text() : '').catch(() => '');

  // Above-ground biomass carbon inventory (2021–2025, WDPA downscaled)
  const biomassCsv = await fetch('/data/biomass_series.csv')
    .then(r => r.ok ? r.text() : '')
    .catch(() => '');

  // Initialize global cluster color lookup (used across all scenes)
  initClusterLookup(clusters);

  new StoryMap(
    app, species, plots, meta, landmarks, comparison,
    clusters, plotSims, climateCsv, mapAdapter, latestCog, soilCsv, gppCsv, biomassCsv,
  );
  new BandOverlay(app, mapAdapter);
}

init().catch(console.error);
